import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';
import { FormikProps } from 'formik';
import * as _ from 'lodash';
import React from 'react';

import {
  ActiveDiffIcon,
  IconButton,
  InactiveDiffIcon,
} from 'packages/common-frontend/components/IconButton';
import {
  UpdateCreditAgreementFullArtifactWipMutationFnGQLType,
  UpdateClientCreditAgreementFullArtifactWipMutationFnGQLType,
} from 'packages/creditAgreements/requests/requests.gql.generated';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { RemoveCreditAgreementWipButton } from 'packages/creditAgreements/ui/creditAgreementWip';
import { CommitCreditAgreementWipButton } from 'packages/creditAgreements/ui/creditAgreementWip/CommitCreditAgreementWipButton';
import { CreditAgreementWipDiffViewer } from 'packages/creditAgreements/ui/creditAgreementWip/CreditAgreementWipDiffViewer';
import { SaveCreditAgreementWipButton } from 'packages/creditAgreements/ui/creditAgreementWip/SaveCreditAgreementWipButton';
import { UploadJsonButton } from 'packages/creditAgreements/ui/creditAgreementWip/UploadJsonButton';
import { UploadJsonModal } from 'packages/creditAgreements/ui/creditAgreementWip/UploadJsonModal';
import { ExportJsonButton } from 'packages/creditAgreements/ui/ExportJsonButton';
import { ExportXlsx2CsvConfigButton } from 'packages/creditAgreements/ui/ExportXlsx2CsvConfig';
import { WaiversExportImportMenu } from 'packages/creditAgreements/ui/WaiversExportImportMenu';

type CreditAgreementWipHeaderProps = {
  isWipDirty: boolean;
  isDiffOpen: boolean;
  setIsDiffOpen: (isOpen: boolean) => void;
  creditFacilityId: string;
  formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
  currentCreditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  artifactDocumentId: string | null;
  creditFacilitySlug: string;
  isJsonUploadOpen: boolean;
  setIsJsonUploadOpen: React.Dispatch<React.SetStateAction<boolean>>;
  operationMode: CreditAgreement.OperationMode;
  saveCreditAgreementFullArtifactWip:
    | UpdateClientCreditAgreementFullArtifactWipMutationFnGQLType
    | UpdateCreditAgreementFullArtifactWipMutationFnGQLType;
  isSaveCreditAgreementFullArtifactWipLoading: boolean;
  onCommitDone: () => void;
  onRemoveWipSuccess?: () => void;
};
export const CreditAgreementWipHeader = ({
  isWipDirty,
  isDiffOpen,
  setIsDiffOpen,
  formProps,
  creditFacilityId,
  artifactDocumentId,
  creditFacilitySlug,
  currentCreditAgreementFullArtifact,
  isJsonUploadOpen,
  setIsJsonUploadOpen,
  operationMode,
  saveCreditAgreementFullArtifactWip,
  isSaveCreditAgreementFullArtifactWipLoading,
  onCommitDone,
  onRemoveWipSuccess,
}: CreditAgreementWipHeaderProps) => {
  const showDiscardWipButton = false;
  return (
    <>
      <Flex mb="40px">
        <Heading size="lg" mr={5}>
          Credit Agreement WIP
        </Heading>
        <IconButton
          ariaLabel="info"
          label={isDiffOpen ? 'Hide Changes' : 'Show Changes'}
          icon={isDiffOpen ? <InactiveDiffIcon /> : <ActiveDiffIcon />}
          onClick={() => setIsDiffOpen(!isDiffOpen)}
        />
        <SaveCreditAgreementWipButton
          creditAgreementFullArtifactWip={formProps.values}
          isDisabled={!!_.keys(formProps.errors).length}
          saveCreditAgreementFullArtifactWip={
            saveCreditAgreementFullArtifactWip
          }
          isSaveCreditAgreementFullArtifactWipLoading={
            isSaveCreditAgreementFullArtifactWipLoading
          }
          operationMode={operationMode}
        />
        <CommitCreditAgreementWipButton
          creditFacilityId={creditFacilityId}
          isDisabled={isWipDirty || !!_.keys(formProps.errors).length}
          operationMode={operationMode}
          onCommitDone={onCommitDone}
        />
        <RemoveCreditAgreementWipButton
          showDiscardWipButton={showDiscardWipButton}
          creditFacilityId={creditFacilityId}
          doesCreditAgreementWipExist={null}
          onSuccess={onRemoveWipSuccess}
        />
        <ExportJsonButton
          creditFacilityId={creditFacilityId}
          creditAgreementFullArtifact={formProps.values} // this used to be modifiedWip, but that static, vs formProps.values ?
        />
        <ExportXlsx2CsvConfigButton
          creditFacilitySlug={creditFacilitySlug}
          creditAgreementFullArtifact={formProps.values}
        />
        {operationMode === 'admin' && (
          <WaiversExportImportMenu creditFacilityId={creditFacilityId} />
        )}
        <UploadJsonButton setIsOpen={setIsJsonUploadOpen} />
        <UploadJsonModal
          setIsOpen={setIsJsonUploadOpen}
          isOpen={isJsonUploadOpen}
          setValues={formProps.setValues}
          creditFacilityId={creditFacilityId}
          artifactDocumentId={artifactDocumentId}
          currentCreditAgreementId={currentCreditAgreementFullArtifact.id}
          operationMode={operationMode}
        />
      </Flex>
      {!!_.keys(formProps.errors).length && (
        <Alert status="warning" display="flex" w="fit-content">
          <AlertIcon />
          <AlertDescription>
            <Text>{JSON.stringify(formProps.errors)}</Text>
          </AlertDescription>
        </Alert>
      )}
      {isDiffOpen && (
        <Box mb="35px">
          <CreditAgreementWipDiffViewer
            creditAgreementFullArtifactWip={formProps.values}
            currentCreditAgreementFullArtifact={
              currentCreditAgreementFullArtifact
            }
          />
        </Box>
      )}
    </>
  );
};
